import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { useUserAuth } from "@/user/UserAuthContext";
import LocalizedLink from "@/components/LocalizedLink";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AUTH_STRINGS } from "@/user/authStrings";
import {
  AuthCard,
  BackLink,
  Field,
  FormAlert,
  GoogleButton,
  OrDivider,
  authErrorMessage,
  zodFieldErrors,
} from "@/pages/auth/AuthLogin";

/**
 * Public sign-up page — hardened alongside sign-in (P1). See AuthLogin.jsx
 * for the failure model; this page follows it exactly.
 */

const SignupSchema = z.object({
  name: z.string().trim().min(1, "nameRequired"),
  email: z.string().trim().min(1, "emailRequired").email("emailInvalid"),
  password: z.string().min(8, "passwordTooShort"),
});

const TOAST_CODES = new Set(["network", "server", "rate_limited"]);

export default function AuthSignup() {
  return (
    <ErrorBoundary scope="section">
      <SignupForm />
    </ErrorBoundary>
  );
}

function SignupForm() {
  const { lang } = useLanguage();
  const L = AUTH_STRINGS[lang] || AUTH_STRINGS.en;
  const { signup, status } = useUserAuth();
  const navigate = useNavigate();
  const accountPath = `${langPrefix(lang)}/account` || "/account";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (status === "authenticated") navigate(accountPath, { replace: true });
  }, [status, accountPath, navigate]);

  const clearFieldError = (field) => {
    if (!fieldErrors[field]) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setFormError(null);

    const parsed = SignupSchema.safeParse({ name, email, password });
    const errors = zodFieldErrors(parsed, L);
    setFieldErrors(errors);
    if (!parsed.success) {
      setFormError(L.formHasErrors);
      return;
    }

    setSubmitting(true);
    try {
      const res = await signup({
        name: parsed.data.name,
        email: parsed.data.email,
        password: parsed.data.password,
        locale_pref: lang,
      });
      if (!res || !res.ok) {
        const message = authErrorMessage(L, res);
        setFormError(message);
        if (res && TOAST_CODES.has(res.code)) toast.error(message);
        return;
      }
      navigate(accountPath, { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[AuthSignup] unexpected failure", err);
      setFormError(L.errGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const formErrorText =
    typeof formError === "string" ? formError : formError ? L.errGeneric : null;

  return (
    <AuthCard eyebrow={L.eyebrow} title={L.signupTitle} body={L.signupBody}>
      <form
        onSubmit={onSubmit}
        className="mt-6 space-y-4"
        data-testid="signup-form"
        noValidate
        aria-busy={submitting || undefined}
        aria-describedby={formErrorText ? "signup-form-error" : undefined}
      >
        <Field
          label={L.name}
          htmlFor="signup-name"
          error={fieldErrors.name}
          errorId="signup-name-error"
          errorTestId="signup-name-error"
        >
          <input
            id="signup-name"
            name="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError("name");
            }}
            className="sas-input"
            data-testid="signup-name"
            aria-invalid={fieldErrors.name ? "true" : undefined}
            aria-describedby={fieldErrors.name ? "signup-name-error" : undefined}
            disabled={submitting}
          />
        </Field>
        <Field
          label={L.email}
          htmlFor="signup-email"
          error={fieldErrors.email}
          errorId="signup-email-error"
          errorTestId="signup-email-error"
        >
          <input
            id="signup-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            className="sas-input"
            data-testid="signup-email"
            dir="ltr"
            aria-invalid={fieldErrors.email ? "true" : undefined}
            aria-describedby={fieldErrors.email ? "signup-email-error" : undefined}
            disabled={submitting}
          />
        </Field>
        <Field
          label={L.password}
          htmlFor="signup-password"
          error={fieldErrors.password}
          errorId="signup-password-error"
          errorTestId="signup-password-error"
        >
          <input
            id="signup-password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError("password");
            }}
            className="sas-input"
            data-testid="signup-password"
            dir="ltr"
            aria-invalid={fieldErrors.password ? "true" : undefined}
            aria-describedby={
              fieldErrors.password ? "signup-password-error signup-password-hint" : "signup-password-hint"
            }
            disabled={submitting}
          />
          <span
            id="signup-password-hint"
            className="block text-[11.5px] text-[color:var(--sas-ink-2)] mt-1.5"
          >
            {L.passwordHint}
          </span>
        </Field>

        {formErrorText && (
          <FormAlert id="signup-form-error" testId="signup-error">
            {formErrorText}
          </FormAlert>
        )}

        <button
          type="submit"
          disabled={submitting}
          aria-disabled={submitting || undefined}
          className="sas-btn sas-btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
          data-testid="signup-submit"
        >
          <UserPlus size={15} aria-hidden />
          {submitting ? L.signupLoading : L.signupCta}
        </button>
      </form>

      <OrDivider label={L.or} />

      <GoogleButton lang={lang} label={L.continueGoogle} testId="signup-google" />

      <div className="mt-6 text-center text-[13px] text-[color:var(--sas-ink-2)]">
        {L.haveAccount}{" "}
        <LocalizedLink
          to="/auth/login"
          className="text-[color:var(--sas-accent)] font-medium hover:underline"
          data-testid="signup-go-login"
        >
          {L.signInHere}
        </LocalizedLink>
      </div>
      <BackLink lang={lang} label={L.backToSite} />
    </AuthCard>
  );
}
